// shared-lib/cabinet-runs/editResolve.js
//
// The EDIT site's re-solve of ONE stored run — what a bay edit, an appliance
// swap, a drag or the uppers toggle does to the cabinetry: rehydrate the
// stored run against the room's walls, materialize it through the one
// materializer (`materializeRun`, with `policyFor(RESOLVE_SITES.EDIT)`), and
// merge what it emits back into the room (`mergeResolvedRuns`), settling the
// fixture groups it moved.
//
// Moved here from the web's `cabinetRunGeometry.js` (which re-exports every
// name it exported) so the SERVER re-solves a run exactly as the browser does:
// a customer's option price for a range of another width is the price of the
// cabinets the browser would re-fit around it
// (claude-docs/2026-10-10-server-pricing-design.md). The scan path's own
// segment resolver (`materializeScanRuns`) stays separate — it builds runs
// fresh from a scan rather than re-solving a stored one.
//
// Pure, apart from the merge diagnostics `reportMergeDiagnostics` logs.

const { buildSegmentWallResolver } = require('./segmentWall');
const { reconcileRunFrames, stampRunFrames } = require('./wallFrame');
const { resolveRunBayFootprints } = require('./bayFootprint');
const {
  baysForRun,
  HOSTED_BAY_KINDS,
  isApplianceKind,
  legacyApplianceFromBay,
  runBayForObject,
  storedRunFromRun,
} = require('./bays');
const { overridesForRun } = require('./bayOverrides');
const { uppersEnabled } = require('./profiles');
const { familyOf } = require('./families');
const { runEndsForResolvedRuns } = require('./runEnds');
const { isDerivedRun } = require('./runSource');
const { runOwnsNoObjects } = require('./ownership');
const { materializeRun } = require('./materializeRun');
const { RESOLVE_SITES, policyFor } = require('./resolvePolicy');
const { applianceObjectIds, isInstallPieceOf } = require('./applianceInstall');
const { findRunByIdOrAlias, runAnswersTo } = require('./runIds');
const { mergeResolvedRuns } = require('./mergeResolvedRuns');
const { planVanityRunAccessories } = require('./vanityRunAccessories');
const {
  resolveGroupsAfterMove,
} = require('../group-customization/settleGroups');
const {
  hydrateRoomOpenings,
} = require('../estimate-pricing/utils/openingSyncUtils');

/**
 * A DERIVED run no surviving object claims — the leftover spec of a kitchen an
 * apply or a reprocess has since replaced. The server no longer writes one
 * (`runsToCarryForward` on an apply; a reprocess keeps no run at all), but
 * rooms already carrying one would otherwise hand it an appliance drop, rebuild
 * it on a room restyle, or offer its ends as link targets — each of which
 * re-emits the superseded kitchen over the live one. (The wall-move regen is
 * not gated: see `regenerateAffectedRuns`.)
 *
 * A HAND-DRAWN run is never stale by this test. It is authored, so a layout
 * apply carries it through owning nothing (the same exemption), and a
 * room-scope restyle re-materializing it is how it comes back.
 */
function isStaleDerivedRun(run, objects) {
  return isDerivedRun(run) && runOwnsNoObjects(run, objects);
}

/**
 * Resolve one run segment's wall. ONE resolver for every run (spec §8): a
 * segment names a ROOM wall and resolves from the room alone; `source` no
 * longer selects an id space.
 */
function resolveSegmentWall(seg, resolver) {
  return resolver.resolve(seg);
}

/** The run's cabinet trim records, each only when the run has it. */
const CABINET_TRIM_KEYS = [
  'cabinetCrown',
  'lightRail',
  'extraMolding',
  // the valance over the sink (group 3c) and the wainscot end panels (group
  // 3d), read off the run by their rules
  'valance',
  'endPanels',
];
const carriedCabinetTrim = (run) =>
  Object.fromEntries(
    CABINET_TRIM_KEYS.filter((k) => run[k] !== undefined).map((k) => [
      k,
      run[k],
    ])
  );

/**
 * Rehydrate a stored run ({segments:[{wallId,startParam,endParam}], appliances})
 * into an in-memory run with resolved engine walls, ready for fillRun.
 */
function rehydrateRun(storedInput, room = null) {
  const resolver = buildSegmentWallResolver(room);
  const resolveWall = (s) => resolveSegmentWall(s, resolver);
  // Lazy wall-frame reconcile: a segment stamped against a wall that has
  // since been extended, re-wound or replaced is rebased (or orphaned)
  // BEFORE its params are read. Runs the pipeline never rebased stay right.
  const { run: storedRun, actions } = reconcileRunFrames(
    storedInput,
    resolveWall
  );
  const orphaned = [];
  const segments = (storedRun.segments || [])
    .map((s, i) => {
      if (s.freestanding) return { ...s, _storedIdx: i }; // carry stored index for panel edits
      if (s.orphaned) {
        orphaned.push(i);
        return null;
      }
      const wall = resolveWall(s);
      return wall
        ? { wall, startParam: s.startParam, endParam: s.endParam }
        : null;
    })
    .filter(Boolean);
  // Bay sizes follow their appliance OBJECTS (bayFootprint.js), so the drag
  // clamp and the drop preview see the size the fill will pack around, and
  // the next write persists it. A bay whose appliance is gone is flagged
  // `dangling` and skipped by the fill — the orphaned-segment treatment:
  // reported, never silently deleted, never silently reserving span.
  const sized = resolveRunBayFootprints(storedRun, room?.objects, {
    flagDangling: Array.isArray(room?.objects),
  });
  const bays = baysForRun(sized);
  const danglingBays = bays.filter((b) => b.dangling).map((b) => b.id);
  const frameActions = actions.filter((a) => a.action !== 'none');
  return {
    id: storedRun.id,
    source: storedRun.source,
    // Carried so provenance stamping reflects whether the run is actually a
    // user edit — a scoped regen re-solves a pristine run without marking it
    // userEdited (only the edit path sets this flag).
    userEdited: storedRun.userEdited,
    segments,
    // Appliance BAYS are the model (layout-relationships step 2); a stored
    // `appliances[]` migrates here at read time. `appliances` is kept as a
    // derived view for the readers that still walk it (the free-standing
    // fill, the drop preview) — never write it back.
    bays,
    appliances: bays
      .filter((b) => isApplianceKind(b.kind))
      .map(legacyApplianceFromBay),
    // Per-run upper-cabinet toggle (default ON — only an explicit `false` is
    // OFF). Carried through so the engine's upper pass sees the stored flag.
    uppers: storedRun.uppers,
    // …and WHERE they hang (`upperBand.js`: clamped to the ceiling or set at
    // a gap over the counter, and the cabinet height). Named here because this function names every
    // field it carries: left out, the spec would be dropped on the next edit
    // of anything and the uppers would snap back to the standard band.
    ...(storedRun.upperSpec ? { upperSpec: storedRun.upperSpec } : {}),
    // The product FAMILY constrains the fill's module widths (phase 2) —
    // without carrying it, every re-fit through this path solved
    // unconstrained while the toast claimed otherwise (browser test 11,
    // 2026-08-31: a 33-inch module survived Stock Modular).
    family: storedRun.family,
    // The dimension PROFILE (kitchen | vanity) sets body depth/height, the
    // counter stack and the uppers default (cabinet-runs/profiles.js).
    // rehydrateRun names every field it carries — dropping this one would
    // silently re-solve every vanity run at kitchen dimensions.
    profile: storedRun.profile,
    // The body DEPTH chosen for this run out of the profile's offered list.
    // Carried for the same reason as `family` and `profile`: dropping it here
    // would re-solve a shallow run at the profile default on the first edit,
    // and the user would watch their 12" run grow back to 24".
    depth: storedRun.depth,
    // The counter's own front OVERHANG, where the run records one. A derived
    // vanity run carries the overhang the scan actually granted (often none),
    // and dropping it here would have the next edit re-derive one from the fit
    // rule — pushing a counter into the strip a toilet stands in, on 26% of
    // scanned vanities. Same reason as `depth` above, and the same gotcha.
    counterOverhang: storedRun.counterOverhang,
    // What the SCAN said, as distinct from what the run is built at — the
    // pair above are the live values and a depth pick overwrites them. Carried
    // for the same reason as everything else named here.
    measured: storedRun.measured,
    // STANDARD SIZES, OR MATCH WHAT IS THERE (`sizing.js`) — the user's own
    // answer, which decides the sink base. Same gotcha as every field above,
    // with a nastier symptom: dropped here, a generated vanity would fall back
    // to the inference that reads it as as-built and the first edit would fuse
    // its standard sink base into one full-width box.
    sizing: storedRun.sizing,
    // Carry the run-level appearance through so `effectiveAppearance` can apply
    // it (without this a run-scope colour/finish never reaches the engine).
    appearance: storedRun.appearance,
    // …and its countertop record (`effectiveCountertopAppearance`), the floor a
    // rebuilt slab is dressed from. Dropped here, a run-scope countertop finish
    // — or the empty record of a new kitchen that left the room's behind
    // (`copiedFromRoom`) — gave way to the room's on the first edit.
    ...(storedRun.countertopAppearance
      ? { countertopAppearance: storedRun.countertopAppearance }
      : {}),
    // The run's under-cabinet light record (`utils/underCabinetLightEdit`).
    // `materializeRun` re-stamps the uppers it emits from it; dropped here,
    // the first wall drag would turn a lit run dark.
    ...(typeof storedRun.underCabinetLight === 'boolean'
      ? { underCabinetLight: storedRun.underCabinetLight }
      : {}),
    // …and its cabinet trim records (`shared-lib/cabinet-trim`): crown, light
    // rail and the counted pieces. `materializeRun` stamps the carriers from
    // the first two; dropped here, the first wall drag would take the crown
    // off every cabinet the re-solve emits fresh.
    ...carriedCabinetTrim(storedRun),
    // overridesForRun migrates any legacy `pins` to the new override shape.
    bayOverrides: overridesForRun(storedRun),
    // What the frame reconcile did, for callers that warn / persist.
    frameActions,
    orphanedSegments: orphaned,
    danglingBays,
  };
}

/**
 * Stamp a STORED run's wall-backed segments with the frame of the wall each
 * resolves to today, so a later reprocess that moves the wall can rebase
 * (see shared-lib/cabinet-runs/wallFrame.js). Every write site of a stored
 * run goes through this.
 */
function stampStoredRun(stored, room = null) {
  const resolver = buildSegmentWallResolver(room);
  return stampRunFrames(stored, (s) => resolveSegmentWall(s, resolver));
}

/**
 * World points of every OTHER run's terminal edges, for `align-end` links
 * that target another run's end (the arms of a U squaring their mouth).
 * @returns {Object<string, { start: {x,z}, end: {x,z} }>}
 */
function runEndsForRoom(room, exceptRunId = null) {
  // Rehydration is the browser's half (a stored run has no resolved walls);
  // the geometry is `runEndsForResolvedRuns`, shared with the server scan
  // path so the two cannot answer differently for one room.
  return runEndsForResolvedRuns(
    (room?.cabinetRuns || [])
      // A stale derived run's ends are not targets: they belong to a kitchen
      // that is no longer there.
      .filter(
        (stored) => stored?.id && !isStaleDerivedRun(stored, room?.objects)
      )
      .map((stored) => rehydrateRun(stored, room)),
    exceptRunId
  );
}

// Structure-level opening arrays carry the canonical geometry (center /
// dimensions); post-Phase-5 ROOM copies are slim FK refs. Prefer the
// `structure*` arrays, fall back to the plain ones, across the collection copy
// then the structure doc.
const OPENING_FIELD_PAIRS = [
  ['structureDoors', 'doors', 'door'],
  ['structureWindows', 'windows', 'window'],
  ['structureOpenings', 'openings', 'opening'],
];

/**
 * Openings (doors/windows/openings) WITH full geometry, deduped. Each is tagged
 * with `_kind` ('door' | 'window' | 'opening') so the engine can apply the
 * right per-type clearance depth (e.g. door swing vs window stand-back).
 */
function getRunOpenings(structureData, structureDataFromCollection) {
  const sources = [structureDataFromCollection, structureData].filter(Boolean);
  const seen = new Set();
  const out = [];
  for (const src of sources) {
    for (const [structField, plainField, kind] of OPENING_FIELD_PAIRS) {
      for (const o of src[structField] || src[plainField] || []) {
        if (!o?.center || !o?.dimensions || !(o.parentWallIds || [])[0])
          continue;
        const key = o.id || o.structureOpeningId;
        if (key && seen.has(key)) continue;
        if (key) seen.add(key);
        out.push({ ...o, _kind: kind });
      }
    }
  }
  return out;
}

/**
 * The openings a run's engine walls can MATCH. Every run resolves its walls to
 * ROOM walls (spec §8), and the engine matches an opening to a wall by strict
 * id (`openings.js`: `o.parentWallIds[0] !== wall.id`) — so the structure-
 * frame copies `getRunOpenings` collects (the only ones carrying geometry)
 * are joined to the room's slim copies through `hydrateRoomOpenings`, the
 * same seam the server's kitchen generator uses. A copy that resolves to
 * nothing is dropped rather than handed to the engine as a zero-width
 * opening at the origin.
 */
function openingsForRun(run, room, openings = []) {
  const byKind = { doors: [], windows: [], openings: [] };
  for (const o of openings) {
    if (o?._kind === 'door') byKind.doors.push(o);
    else if (o?._kind === 'window') byKind.windows.push(o);
    else byKind.openings.push(o);
  }

  const hydrated = hydrateRoomOpenings(room, byKind);
  const out = [];
  for (const [, roomField, kind] of OPENING_FIELD_PAIRS) {
    for (const o of hydrated?.[roomField] || []) {
      // A copy that resolved to nothing stays slim; drop it rather than hand
      // the engine an opening it would measure as zero-width at the origin.
      if (!o?.center || !o?.dimensions || !(o.parentWallIds || [])[0]) continue;
      out.push({ ...o, _kind: kind });
    }
  }
  return out;
}

/**
 * The web EDIT site's call into the ONE materializer
 * (`@shared-lib/cabinet-runs/materializeRun`). Everything the engine needs is
 * derived from the room and the policy; what only the browser knows — the
 * openings joined from the structure frame, the other runs' ends — is threaded
 * through `ctx`, and the panel's read-back stamps are applied after.
 *
 * `opts.cabinetDepth` names the depth to build the bays at, overriding the
 * measured depth a scanned run would otherwise keep (`runCabinetDepth`). Only
 * the minimal-change kitchen passes it — a re-solve at the STANDARD depth is
 * the whole point of that option, and it is the one answer the measured-depth
 * rule exists to refuse.
 */
function materializeRunObjects(run, room, openings = [], opts = {}) {
  const out = materializeRun(
    run,
    room,
    {
      openings: openingsForRun(run, room, openings),
      runEnds: runEndsForRoom(room, run.id),
      ...(opts.cabinetDepth != null ? { cabinetDepth: opts.cabinetDepth } : {}),
    },
    policyFor(RESOLVE_SITES.EDIT, run)
  );
  // UI read-back stamps on the wall-backed objects (the engine stamps
  // neither): the panel reads the run's uppers state and its product FAMILY
  // (#1493) off any of its objects. Free-standing objects carry
  // `freeSegIndex` instead. An explicit stored flag wins; unset falls to the
  // run's PROFILE default (kitchen ON, vanity OFF) — the same gate fillRun's
  // upper pass uses, so the toggle cannot read ON for a run that emitted none.
  const runUppers = uppersEnabled(run);
  const runFamily = familyOf(run).id;
  for (const o of out.objects) {
    if (o.freeSegIndex !== undefined) continue;
    o.runUppers = runUppers;
    o.runFamily = runFamily;
  }
  return out;
}

/**
 * Whether an appliance edit asks its run to re-solve: a different MODEL
 * always does (the product may be another size, or another height a hood is
 * seated from), and so does a size change of more than a millimetre on any
 * axis. A move alone never does — the run takes over an appliance's position
 * only when the user edits it.
 */
function applianceSizeChanged(prev, next) {
  if (!prev || !next) return false;
  if (modelChanged(prev, next)) return true;
  const a = prev.dimensions || {};
  const b = next.dimensions || {};
  return ['x', 'y', 'z'].some(
    (k) => Math.abs((a[k] || 0) - (b[k] || 0)) > 0.001
  );
}

/**
 * Whether an edit put a different MODEL on the object. A hood's wood build
 * (`hoodSku`, a Venus wood hood) is its model too: wood ↔ metal is a product
 * change, so the run re-seats the hood and no size the pick wrote is recorded
 * as the user's (`recordInstallPieceSize`).
 */
function modelChanged(prev, next) {
  return (
    (prev?.modelId || null) !== (next?.modelId || null) ||
    (prev?.hoodSku || null) !== (next?.hoodSku || null)
  );
}

/**
 * Whether the edit from `prev` to `next` is a RUN operation, given `room`
 * (which already holds `next`).
 *
 * A bay-claimed appliance re-solves on any size change (#1754): its bay is
 * sized from it, so the cabinetry beside it has to move. An INSTALL piece —
 * a hood, a fridge surround — re-solves on a new MODEL, which the run
 * re-seats off its appliance (`runForInstallPiece`), and a hood also on a new
 * WIDTH (`hoodWidthChanged`). Its height, its depth and a surround's size are
 * the user's to set and move nothing else: re-solving on them rebuilt the
 * run around a piece no bay is sized from (a hood set to 30" in the panel
 * came straight back at 24" with the rest of its run rebuilt around it).
 */
function changeResolvesRun(room, prev, next) {
  if (!applianceSizeChanged(prev, next)) return false;
  return (
    modelChanged(prev, next) ||
    hoodWidthChanged(prev, next) ||
    !!runBayForObject(room, next)
  );
}

/**
 * Whether a range hood came out of an edit at a new width. A hood wider
 * than its range overhangs the bays beside it, where uppers hang, so its run
 * has to re-solve for them to make room (`hoodSkySpans`); the width the user
 * set is recorded first, so the re-solve keeps it (`recordInstallPieceSize`).
 * A generic size tile (30" / 36" / 48") writes no new `modelId`, only the
 * size, so this is what re-solves it too.
 */
function hoodWidthChanged(prev, next) {
  if (next?.type !== 'vent-hood') return false;
  return (
    Math.abs((prev?.dimensions?.x || 0) - (next?.dimensions?.x || 0)) > 0.001
  );
}

/**
 * The stored run an INSTALL piece belongs to — a range's vent hood, a
 * fridge's surround or over-fridge cabinet — found through the appliances it
 * serves, since the piece itself carries no `bayId`. Null when it serves none.
 */
function runForInstallPiece(room, object) {
  if (!object?.id) return null;
  for (const stored of room?.cabinetRuns || []) {
    const ids = applianceObjectIds(stored);
    if (!ids.size) continue;
    const appliances = (room?.objects || []).filter(
      (o) => o?.id && ids.has(o.id)
    );
    if (appliances.length && isInstallPieceOf(object, appliances))
      return stored;
  }
  return null;
}

/**
 * A size change on a bay-claimed appliance is a RUN operation (#1754): the
 * run re-solves around the object's new size and its cabinetry follows —
 * the same commit shape as a bay override (`rematerializeWithOverrides`).
 * Hosted kinds (sink-base, cooktop) are excluded: their reservation is the
 * base cabinet, and a basin swap leaves `dimensions` alone by design.
 *
 * `room` must already hold the CHANGED object — the fill reuses it
 * (`existingObjects`) and sizes the bay from it (bayFootprint.js).
 *
 * @returns {{ objects, cabinetRuns, resolve }|null} null when the object is
 *   in no bay, in a hosted bay, or in a stale derived run.
 */
function resolveRunAfterObjectChange(room, objectId, openings = []) {
  const object = (room?.objects || []).find((o) => o?.id === objectId);
  const hit = object ? runBayForObject(room, object) : null;
  if (hit && HOSTED_BAY_KINDS.has(hit.bay.kind)) return null;
  // An INSTALL piece is the run's cabinetry too: swapping a hood for a
  // product changes its height, and its seat is measured from the RANGE top
  // (`buildVentHood`), not from where the old one's centre happened to be.
  // Only a run re-solve re-seats it, and the piece carries no `bayId`, so
  // `runBayForObject` alone left the clearance off by half the height
  // difference until some unrelated edit corrected it.
  const stored =
    hit?.stored || (object ? runForInstallPiece(room, object) : null);
  if (!stored) return null;
  if (isStaleDerivedRun(stored, room.objects)) return null;
  // Same provenance as a bay edit: an auto run becomes userEdited and a scan
  // run becomes the user's (`regeneratesOnWallMove`).
  const storedForSolve = isDerivedRun(stored)
    ? { ...stored, userEdited: true }
    : stored;
  const run = rehydrateRun(storedForSolve, room);
  const resolve = materializeRunObjects(run, room, openings);
  // Persist the bays as rehydrated — sized from the object — so the stored
  // fallback stays current.
  const storedRun = stampStoredRun(
    storedRunFromRun({ ...storedForSolve, bays: run.bays }),
    room
  );
  return {
    objects: objectsAfterRunResolve(room, stored.id, resolve.objects, resolve),
    cabinetRuns: (room.cabinetRuns || []).map((cr) =>
      cr.id === stored.id ? storedRun : cr
    ),
    resolve,
  };
}

/**
 * The stored run behind an id or alias, for a merge with no resolve in hand.
 *
 * THROWS when the room does not list it. The fallback used to be
 * `{ id: runId }`, and `policyFor` reads a run with no `source` as HAND-DRAWN:
 * uppers 'rebuild', `emitAppliances` true. So a bare sweep of a scan run the
 * room no longer carries would have deleted its MEASURED uppers and emitted
 * appliances over its measured ones — silently, on nothing more than a stale
 * id (ledger 57). Every caller passes the room that still lists the run
 * (`deleteRun` resolves against the pre-filter room; the wall-move regen passes
 * the healed room its own `affectedRunIds` came from).
 *
 * `because` says WHY the run is needed, because the delete path needs it for a
 * different reason than the merge does — there the run carries the bays, and a
 * delete that could not read them would leave every appliance standing.
 */
function storedRunFor(
  room,
  runId,
  where,
  because = 'and no resolve was given'
) {
  const run = findRunByIdOrAlias(room?.cabinetRuns || [], runId);
  if (!run) {
    throw new Error(
      `${where}: run ${runId} is not in room.cabinetRuns ${because}`
    );
  }
  return run;
}

/**
 * Everything the merge REPORTED, said where a developer looks.
 *
 * Two lists, and the web used to drop one of them. A §6 ownership violation is
 * a MODEL bug — a run emitting an appliance it only positions, an object a bay
 * names that carries no `bayId`, a tag naming a run that is gone — not user
 * data and not something to refuse an edit over. `warnings` is the incumbent
 * pass's own findings — a tall unit that keeps its product width, one that
 * could not be seated onto its bay, a tall body the sweep took that nothing
 * re-emits — which the server persists into `stats.runPipelineWarnings` and
 * the web threw away entirely (ledger 85).
 */
function reportMergeDiagnostics(result) {
  for (const v of result.violations || []) {
    // A model bug, not user data: say so where a developer looks.
    console.warn('[cabinet-runs] ownership contract:', v);
  }
  for (const w of result.warnings || []) {
    console.warn('[cabinet-runs] merge:', w);
  }
}

/**
 * The panel's read-back stamps, applied AFTER the merge.
 *
 * `CabinetRunSection` reads a run's uppers state (default-ON) and its product
 * FAMILY off the SELECTED object, and the merge replaces the fill's stand-in
 * with the INCUMBENT out of `room.objects`: a scanned sink host carries neither
 * field, and a manual/auto one carries the PREVIOUS solve's values. Stamping
 * only `materializeRunObjects`' own output therefore left the panel reading a
 * stale answer on exactly the objects the incumbent pass re-seats. Wall-backed
 * objects only — a free-standing one carries `freeSegIndex` and is read back
 * through its segment instead.
 */
function stampRunReadback(objects, run) {
  if (!run?.id) return objects;
  // Same profile-aware gate as the stamp in `materializeRunObjects`.
  const runUppers = uppersEnabled(run);
  const runFamily = familyOf(run).id;
  return (objects || []).map((o) =>
    o?.runId && o.freeSegIndex === undefined && runAnswersTo(run, o.runId)
      ? { ...o, runUppers, runFamily }
      : o
  );
}

/**
 * The room's objects after a vanity-profile run's GROUP-OWNED accessories
 * have followed it: the mirror over each basin, the light over each mirror.
 *
 * Sits outside the merge on purpose. An accessory carries a `fixtureGroupId`
 * and never a `runId`, so the merge's keep rule leaves it alone — which is
 * what makes a user's swapped mirror survive every re-solve — and that same
 * rule means the run can never move it either. This is the pass that does
 * (`shared-lib/cabinet-runs/vanityRunAccessories.js`); a kitchen run gets its
 * objects back by identity.
 *
 * `priorObjects` are the room's objects BEFORE the solve, which is what a
 * move is measured against. Omit them on a fresh draw, where there is nothing
 * to move and only the top-up runs.
 */
function withVanityRunAccessories(room, runs, objects, priorObjects) {
  let next = objects;
  for (const run of runs || []) {
    if (!run) continue;
    next = planVanityRunAccessories({
      room: { ...room, objects: next },
      run,
      priorObjects,
      roomId: room?.id,
    }).objects;
  }
  return next;
}

/**
 * The room's objects after ONE run re-solves — the single merge point for bay
 * edits, drags, the uppers toggle, the appliance drop, the run delete. `resolve`
 * is `materializeRunObjects`'s result (may be absent for a bare sweep);
 * `newObjects` is what to write — usually `resolve.objects`, restyled or
 * extended by the caller.
 */
function objectsAfterRunResolve(room, runId, newObjects, resolve) {
  const run =
    resolve?.run || storedRunFor(room, runId, 'objectsAfterRunResolve');
  const result = mergeResolvedRuns(
    room,
    { resolves: [{ ...(resolve || {}), run }], emitted: newObjects || [] },
    RESOLVE_SITES.EDIT,
    { settleGroups: resolveGroupsAfterMove }
  );
  reportMergeDiagnostics(result);
  return withVanityRunAccessories(
    room,
    [run],
    stampRunReadback(result.objects, run),
    room?.objects
  );
}

module.exports = {
  isStaleDerivedRun,
  rehydrateRun,
  stampStoredRun,
  runEndsForRoom,
  getRunOpenings,
  openingsForRun,
  materializeRunObjects,
  runBayForObject,
  applianceSizeChanged,
  modelChanged,
  changeResolvesRun,
  hoodWidthChanged,
  runForInstallPiece,
  resolveRunAfterObjectChange,
  storedRunFor,
  reportMergeDiagnostics,
  stampRunReadback,
  withVanityRunAccessories,
  objectsAfterRunResolve,
};
